/**
 * Clients → Documents — prototype VIEWS.documents + docTable + uploadDoc/saveDoc (docs/prototype.html 1322–1328):
 * head action "Upload a file", type pills All | MIS | Decks | Reports | Plans | RCAs, and one table of
 * Document / Client / Type chip (TYPEC) / Version / Updated / By / Open.
 *
 * Data: GET /documents?doc_type (the server scopes it: managers all clients, a member their SPOC clients).
 * Upload: POST /documents/sign-upload → PUT → POST /documents with file_key, named after the file (prototype).
 * Open: GET /documents/{id} for the newest version with a file → GET …/versions/{vid}/file (a 10-minute link);
 * enabled for a later revision without its file yet, since an earlier version may have one.
 */
import { useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router';
import { DocType, type DocumentListItem } from '@amplispot/contracts';
import { nameFromFile, openDocumentFile, TYPE_CHIP, useCreateDocument, useDocuments } from '../../lib/documents';
import { useClients } from '../../lib/data';
import { useSession } from '../../lib/session';
import { dayMonth } from '../../lib/timesheets';
import { Avatar, ErrorState, Field, Loading, Modal, Pills, errorText, useToast } from '../../ui/kit';

type TypeFilter = 'all' | DocType;
const TYPE_PILLS: [TypeFilter, string][] = [
  ['all', 'All'],
  ['MIS', 'MIS'],
  ['Deck', 'Decks'],
  ['Report', 'Reports'],
  ['Plan', 'Plans'],
  ['RCA', 'RCAs'],
];

export const clientLinkStyle: CSSProperties = { color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' };

/** Prototype `clientLink`: the client's name to its page, with ▲ for a high-priority client. */
export function ClientLink({ client }: { client: DocumentListItem['client'] }) {
  return (
    <>
      <Link to={`/clients/book?client=${encodeURIComponent(client.id)}`} style={clientLinkStyle} onClick={(e) => e.stopPropagation()}>
        {client.name}
      </Link>
      {client.priority === 'high' ? (
        <span className="pm" title="High priority client">
          {' '}▲
        </span>
      ) : null}
    </>
  );
}

/**
 * `has_file` covers only the current revision; after "Changes requested" opens an empty revision, v1…vN-1 may still
 * hold files. Open then takes the newest version with a file (openDocumentFile), or says there is none yet.
 */
const mayHaveFile = (d: DocumentListItem) => d.has_file || d.revision > 1;

export function Documents() {
  const { can, readOnly } = useSession();
  const [sp, setSp] = useSearchParams();
  const t = (TYPE_PILLS.map((p) => p[0]).find((k) => k === sp.get('type')) ?? 'all') as TypeFilter;
  const q = useDocuments(t === 'all' ? {} : { doc_type: t });
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  const write = !readOnly && (can('clients.read.all') || can('clients.read.own'));

  const setType = (v: TypeFilter) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      if (v === 'all') n.delete('type');
      else n.set('type', v);
      return n;
    });

  const open = (d: DocumentListItem) => openDocumentFile(d.id).catch((e) => toast(errorText(e)));

  return (
    <>
      {write ? (
        <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
          <div className="r" style={{ marginLeft: 0 }}>
            <button className="btn pri" onClick={() => setUploading(true)}>
              Upload a file
            </button>
          </div>
        </div>
      ) : null}
      <div className="bar">
        <Pills<TypeFilter> options={TYPE_PILLS} value={t} onChange={setType} />
      </div>
      {q.isPending ? (
        <Loading label="Loading the documents…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Document</th>
                <th>Client</th>
                <th>Type</th>
                <th>Version</th>
                <th>Updated</th>
                <th>By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((d) => (
                <tr key={d.id}>
                  <td>
                    <b>{d.name}</b>
                  </td>
                  <td>
                    <ClientLink client={d.client} />
                  </td>
                  <td>
                    <span className={`chip ${TYPE_CHIP[d.doc_type]}`}>{d.doc_type}</span>
                  </td>
                  <td className="num">v{d.revision}</td>
                  <td>{dayMonth(d.updated_at.slice(0, 10))}</td>
                  <td>
                    <Avatar user={d.owner} />
                  </td>
                  <td className="tr">
                    <button className="btn sm" onClick={() => void open(d)} disabled={!mayHaveFile(d)} title={mayHaveFile(d) ? undefined : 'No file uploaded yet'}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
              {!q.data.rows.length ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">
                      <b>No documents</b>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      {uploading ? <UploadModal onClose={() => setUploading(false)} initialType={t === 'all' ? 'MIS' : t} /> : null}
    </>
  );
}

/** Prototype `uploadDoc`: Client, Type, File → Upload. The document is named after the file. */
function UploadModal({ onClose, initialType }: { onClose: () => void; initialType: DocType }) {
  const clients = useClients();
  const create = useCreateDocument();
  const toast = useToast();
  const [client, setClient] = useState('');
  const [type, setType] = useState<DocType>(initialType);
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<Record<string, string>>({});
  const list = clients.data ?? [];
  const clientId = client || list[0]?.id || '';

  const save = () => {
    if (!file) {
      setErr({ file: 'Choose a file' });
      return toast('Choose a file');
    }
    if (!clientId) return toast('Pick a client');
    create.mutate(
      { body: { client_id: clientId, name: nameFromFile(file.name), doc_type: type }, file },
      {
        onSuccess: () => {
          toast('Uploaded');
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  };

  return (
    <Modal
      title="Upload a file"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={create.isPending}>
            {create.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </>
      }
    >
      <Field label="Client">
        <select className="in" value={clientId} onChange={(e) => setClient(e.target.value)} disabled={clients.isPending}>
          {clients.isPending ? <option>Loading clients…</option> : null}
          {list.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Type">
        <select className="in" value={type} onChange={(e) => setType(e.target.value as DocType)}>
          {DocType.options.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>
      <Field label="File" error={err.file}>
        <input
          type="file"
          className="in"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setErr({});
          }}
        />
      </Field>
    </Modal>
  );
}
